import { create } from 'zustand';
import {
    FileUploadItem,
    FileUploadStatus,
    CameraCreationItem,
    CameraCreationStatus,
} from '@/types/source-creation';

interface SourceCreationStore {
    fileItems: FileUploadItem[];
    fileDrawerExpanded: boolean;
    cameraItems: CameraCreationItem[];
    cameraDrawerExpanded: boolean;
    targetFolderId: string | null;
    targetReportId: string | null;
    addFileItems: (
        items: Omit<
            FileUploadItem,
            | 'id'
            | 'status'
            | 'progress'
            | 'retryCount'
            | 'targetFolderId'
            | 'targetReportId'
        >[],
        targetFolderIdOverride?: string | null,
    ) => void;
    updateFileItem: (id: string, updates: Partial<FileUploadItem>) => void;
    removeFileItem: (id: string) => void;
    clearAllFiles: () => void;
    setFileDrawerExpanded: (expanded: boolean) => void;
    addCameraItems: (
        items: Omit<CameraCreationItem, 'id' | 'status' | 'targetFolderId'>[],
        targetFolderId?: string | null,
    ) => void;
    updateCameraItem: (
        id: string,
        updates: Partial<CameraCreationItem>,
    ) => void;
    removeCameraItem: (id: string) => void;
    clearAllCameras: () => void;
    setCameraDrawerExpanded: (expanded: boolean) => void;
    setTargetFolderId: (folderId: string | null) => void;
    setTargetReportId: (reportId: string | null) => void;
}

export const useSourceCreationStore = create<SourceCreationStore>((set) => ({
    fileItems: [],
    fileDrawerExpanded: true,
    cameraItems: [],
    cameraDrawerExpanded: true,
    targetFolderId: null,
    targetReportId: null,

    setTargetFolderId: (folderId) => set({ targetFolderId: folderId }),
    setTargetReportId: (reportId) => set({ targetReportId: reportId }),

    addFileItems: (newItems, targetFolderIdOverride) =>
        set((state) => {
            const existingFingerprints = new Set(
                state.fileItems
                    .filter(
                        (item) => item.status !== FileUploadStatus.COMPLETED,
                    )
                    .map((item) => item.fingerprint),
            );

            const uniqueItems = newItems.filter(
                (item) => !existingFingerprints.has(item.fingerprint),
            );

            const folderId = targetFolderIdOverride ?? state.targetFolderId;
            const reportId = state.targetReportId;
            const itemsToAdd: FileUploadItem[] = uniqueItems.map((item) => ({
                ...item,
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                status: FileUploadStatus.PENDING,
                progress: 0,
                retryCount: 0,
                targetFolderId: folderId,
                targetReportId: reportId,
            }));

            return {
                fileItems: [...state.fileItems, ...itemsToAdd],
                fileDrawerExpanded: true,
            };
        }),

    updateFileItem: (id, updates) =>
        set((state) => ({
            fileItems: state.fileItems.map((item) =>
                item.id === id ? { ...item, ...updates } : item,
            ),
        })),

    removeFileItem: (id) =>
        set((state) => ({
            fileItems: state.fileItems.filter((item) => item.id !== id),
        })),

    clearAllFiles: () => set({ fileItems: [] }),

    setFileDrawerExpanded: (expanded) => set({ fileDrawerExpanded: expanded }),

    addCameraItems: (newItems, targetFolderId) =>
        set((state) => {
            const itemsToAdd: CameraCreationItem[] = newItems.map((item) => ({
                ...item,
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                status: CameraCreationStatus.PENDING,
                targetFolderId: targetFolderId ?? null,
            }));

            return {
                cameraItems: [...state.cameraItems, ...itemsToAdd],
                cameraDrawerExpanded: true,
            };
        }),

    updateCameraItem: (id, updates) =>
        set((state) => ({
            cameraItems: state.cameraItems.map((item) =>
                item.id === id ? { ...item, ...updates } : item,
            ),
        })),

    removeCameraItem: (id) =>
        set((state) => ({
            cameraItems: state.cameraItems.filter((item) => item.id !== id),
        })),

    clearAllCameras: () => set({ cameraItems: [] }),

    setCameraDrawerExpanded: (expanded) =>
        set({ cameraDrawerExpanded: expanded }),
}));
